// Push Notification Service for Web Push API

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ibexgames.fun/api';

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;
  private vapidPublicKey: string | null = null;

  // Check if push notifications are supported
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // Check current permission status
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('Push notifications are not supported');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Get VAPID public key from server
  async getVapidPublicKey(): Promise<string | null> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/push/vapid-public-key`);
      const data = await response.json();
      this.vapidPublicKey = data.publicKey;
      console.log('VAPID public key fetched from server');
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Failed to get VAPID public key:', error);
      return null;
    }
  }

  // Register service worker
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isSupported()) {
      console.warn('Service workers are not supported');
      return null;
    }

    try {
      // Check if already registered
      const existingRegistration = await navigator.serviceWorker.getRegistration('/');
      if (existingRegistration) {
        this.registration = existingRegistration;
        console.log('Service Worker already registered');
        return this.registration;
      }

      // Register new service worker
      this.registration = await navigator.serviceWorker.register(
        '/service-worker.js',
        { scope: '/' }
      );

      console.log('Service Worker registered:', this.registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service Worker is ready');

      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  // Subscribe to push notifications
  async subscribe(): Promise<PushSubscription | null> {
    try {
      if (!this.registration) {
        this.registration = await this.registerServiceWorker();
      }

      if (!this.registration) {
        console.error('No service worker registration available');
        return null;
      }

      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        console.log('Already subscribed to push notifications');
        return this.subscription;
      }

      // Get VAPID public key from server
      const vapidKey = await this.getVapidPublicKey();
      if (!vapidKey) {
        console.error('Failed to get VAPID public key');
        return null;
      }

      // Subscribe to push notifications
      const vapidPublicKey = this.urlBase64ToUint8Array(vapidKey);
      
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });

      console.log('Subscribed to push notifications:', this.subscription);
      return this.subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(): Promise<boolean> {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  // Get current subscription
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      this.registration = await navigator.serviceWorker.ready;
    }

    if (this.registration) {
      this.subscription = await this.registration.pushManager.getSubscription();
    }

    return this.subscription;
  }

  // Initialize push notifications (request permission and subscribe)
  async initialize(): Promise<{
    success: boolean;
    subscription: PushSubscription | null;
    permission: NotificationPermission;
  }> {
    if (!this.isSupported()) {
      return {
        success: false,
        subscription: null,
        permission: 'denied',
      };
    }

    try {
      // Request permission
      const permission = await this.requestPermission();
      
      if (permission !== 'granted') {
        return {
          success: false,
          subscription: null,
          permission,
        };
      }

      // Register service worker and subscribe
      const subscription = await this.subscribe();

      return {
        success: subscription !== null,
        subscription,
        permission,
      };
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      return {
        success: false,
        subscription: null,
        permission: this.getPermissionStatus(),
      };
    }
  }

  // Convert VAPID key to Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Show a test notification
  async showTestNotification(): Promise<void> {
    if (this.getPermissionStatus() !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    if (!this.registration) {
      this.registration = await navigator.serviceWorker.ready;
    }

    if (this.registration) {
      await this.registration.showNotification('Test Notification', {
        body: 'Push notifications are working!',
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'test',
        vibrate: [200, 100, 200],
      });
    }
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;

